import { Check, HelpCircle, X } from "lucide-react";

import { cn } from "@/lib/utils";

// ===========================================================================
// El cotejo: dato por dato, qué coincide y qué no.
//
// Las dos columnas de la revisión enseñan los dos papeles y dejan la
// comparación en los ojos de quien revisa. Con una glosa de sesenta caracteres
// y dos números de operación de siete dígitos, eso es pedirle que haga de
// lector óptico: en el primer caso real que se miró, el banco decía 5394085 y
// el voucher 5394081 —difieren en el último dígito— y el resumen de arriba
// decía «el voucher, la boleta y el abono dicen lo mismo».
//
// Esto no decide: dice qué cuadra, qué no y qué no se puede afirmar. Quien
// revisa sigue decidiendo, que para eso está.
//
// Las TRES situaciones se ven distintas a propósito. «No se puede comprobar»
// no es «no coincide»: pintarlas iguales culparía al expediente de un dato que
// el banco no escribió, y quien revisa acabaría descartando pagos buenos.
// ===========================================================================

type Fila = {
  clave: string;
  titulo: string;
  veredicto: "coincide" | "difiere" | "no_se_puede";
  dice: string;
  enElBanco: string | null;
  enElVoucher: string | null;
};

const SENA = {
  coincide: {
    Icono: Check,
    color: "text-emerald-700 dark:text-emerald-400",
    fondo: "bg-emerald-500/10",
  },
  difiere: {
    Icono: X,
    color: "text-red-700 dark:text-red-400",
    fondo: "bg-red-500/10",
  },
  no_se_puede: {
    Icono: HelpCircle,
    color: "text-muted-foreground",
    fondo: "bg-muted",
  },
} as const;

export function Cotejo({ filas }: { filas: Fila[] }) {
  if (filas.length === 0) return null;

  const difieren = filas.filter((f) => f.veredicto === "difiere").length;
  const coinciden = filas.filter((f) => f.veredicto === "coincide").length;
  const sinComprobar = filas.filter(
    (f) => f.veredicto === "no_se_puede",
  ).length;

  return (
    <div
      className={cn(
        "mt-4 overflow-hidden rounded-lg border",
        difieren > 0 && "border-amber-600/40",
      )}
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b bg-muted/40 px-3 py-2">
        <span className="font-semibold text-[11px] text-muted-foreground uppercase tracking-wider">
          Qué coincide
        </span>
        <span className="text-[12px] tabular-nums">
          <b className="text-emerald-700 dark:text-emerald-400">{coinciden}</b>{" "}
          <span className="text-muted-foreground">
            cuadran
            {difieren > 0 ? (
              <>
                {" · "}
                <b className="text-red-700 dark:text-red-400">{difieren}</b> no
              </>
            ) : null}
            {sinComprobar > 0 ? ` · ${sinComprobar} sin comprobar` : ""}
          </span>
        </span>
      </div>

      <ul className="divide-y">
        {filas.map((f) => {
          const s = SENA[f.veredicto];
          return (
            <li
              key={f.clave}
              className={cn(
                "flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-3 py-1.5 text-[12.5px]",
                f.veredicto === "difiere" && "bg-red-500/5",
              )}
            >
              <span
                className={cn(
                  "flex size-4 shrink-0 translate-y-0.5 items-center justify-center rounded-full",
                  s.fondo,
                )}
              >
                <s.Icono className={cn("size-3", s.color)} />
              </span>
              <b className="w-[112px] shrink-0 font-medium">{f.titulo}</b>
              <span
                className={cn(
                  "min-w-0 flex-1",
                  f.veredicto === "difiere"
                    ? "text-red-700 dark:text-red-400"
                    : "text-muted-foreground",
                )}
              >
                {f.dice}
              </span>
              {/* Los dos valores, para poder mirarlos sin subir. En
                  monoespaciado: se cotejan dígito a dígito. */}
              {f.enElBanco || f.enElVoucher ? (
                <span className="shrink-0 font-mono text-[11.5px] text-muted-foreground">
                  {f.enElBanco ?? "—"}
                  <span className="mx-1 text-border">/</span>
                  {f.enElVoucher ?? "—"}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
